import { useCallback, useEffect, useState } from 'react';
import { Observable } from 'rxjs';

import { SortingProps } from '@block-explorer/ui';

import { PaginationProps } from '../components';
import {
  getBlockByHashAsync,
  getPaginatedBlocksAsync,
} from '../redux/paginated-blocks/actions';
import {
  getDeployByHashAsync,
  getPaginatedDeploysAsync,
} from '../redux/paginated-deploys/actions';
import { usePaginationState } from '../router/navigation';
import {
  addSignatureToMultisigDeployRequestSubscription,
  addSignatureToMultisigTransactionRequestSubscription,
  adsContactRequestSubscription,
  createActivityExportDeployRequestSubscription,
  getAccountActivityDeployStatusesSubscription,
  getAccountBalanceByPublicKeySubscription,
  getAccountCep18TokenListSubscription,
  getAccountInfoSubscription,
  getAccountsPublicKeySubscription,
  getBidderDetailsDataSubscription,
  getContractInfoSubscription,
  getContractPackageCep18TokenListSubscription,
  getContractPackageInfoSubscription,
  getCsprNameInfoRequestSubscription,
  getCurrencyRateSubscription,
  getDelegatorsTotalRewardsSubscription,
  getDeployRawDataSubscription,
  getFriendlyMarketTokenPairBatchSubscription,
  getKeysToWhichAccountAssociatedSubscription,
  getMultisigTransactionRequestSubscription,
  getNetworkStatsSubscription,
  getPaginatedAccountDelegationsSubscription,
  getPaginatedAccountDelegatorRewardsSubscription,
  getPaginatedAccountDeploysSubscription,
  getPaginatedAccountTokensByContractPackageHashSubscription,
  getPaginatedAccountTokensSubscription,
  getPaginatedAccountTokensTxsListSubscription,
  getPaginatedAccountTransfersSubscription,
  getPaginatedActivityExportRequestSubscription,
  getPaginatedBlockDeploysSubscription,
  getPaginatedBlockTransfersSubscription,
  getPaginatedContractPackageNftOwnersSubscriptions,
  getPaginatedContractPackagesSubscription,
  getPaginatedContractPackageTokensSubscription,
  getPaginatedContractPackageTokensTxsListSubscription,
  getPaginatedDailyDeployNumbersSubscription,
  getPaginatedDeployTransfersSubscription,
  getPaginatedNftTokenActivitySubscription,
  getPaginatedUrefDelegationsRequestSubscription,
  getPaginatedUrefRewardsRequestSubscription,
  getPaginatedValidatorAuctionDataSubscription,
  getPaginatedValidatorBlocksSubscription,
  getPaginatedValidatorEraDataSubscription,
  getPaginatedValidatorRewardsSubscription,
  getRatesHistorySubscription,
  getUrefDelegatorsTotalRewardsSubscription,
  getUrefTransfersRequestSubscription,
  getUrefUndelegationBalanceSubscription,
  getValidatorAuctionMetricsSubscription,
  getValidatorsDelegationsSubscription,
  getValidatorsTotalDelegatorRewardsSubscription,
  getValidatorsTotalRewardsSubscription,
  PaginatedResponse,
  PaginationParams,
  postAccountSubscription,
  postActivityExportRequestSubscription,
  PostRequestParams,
  RequestParams,
  submitActivityExportDeployRequestSubscription,
  submitMultisigDeployRequestSubscription,
  submitMultisigTransactionRequestSubscription,
  useGetContractNftTokenSubscription,
  BatchGetRequestParams,
} from '../services/event-store-service';

import {
  createGetByHashReducerHook,
  createPaginatedGetReducerHook,
} from '../redux/utils';
import { ErrorMessages } from '../domain/error-messages';
import { getAccountDelegationsAsync } from '../redux/account-delegations';
import { getActiveAccountDelegationsAsync } from '../redux/active-account-delegations';
import { isObjEmpty } from '../utils/object';

export type ApiPaginatedGetResult<Entity> = {
  loading: boolean; // TODO: add support
  error: string | null; // TODO: add support
  data: Entity[] | null;
  paginationProps: PaginationProps;
  sortingProps: SortingProps;
  reloadData: () => void;
};

const createPaginatedGetHook =
  <Entity extends any, Params extends RequestParams>(
    paginationStateKey: string,
    stream: (params: Params) => Observable<PaginatedResponse<Entity>>
  ) =>
  (
    requestParams: (Params & Partial<PaginationParams>) | null
  ): ApiPaginatedGetResult<Entity> => {
    const [requestCounter, setRequestCounter] = useState(0);
    const [payload, setPayload] = useState<{
      data: PaginatedResponse<Entity> | null;
      loading: boolean;
      error: string | null;
    }>({
      data: null,
      loading: true,
      error: null,
    });

    const reloadData = useCallback(() => {
      setRequestCounter((state) => state + 1);
    }, []);

    const {
      currentPage: currentPageParam,
      perPage: perPageParam,
      setCurrentPage: setPaginationPage,
      setPerPage: setPaginationLimit,
      orderBy: orderByParam,
      orderDirection: orderDirectionParam,
      setOrder,
    } = usePaginationState(paginationStateKey);

    const { perPage, currentPage, orderBy, orderDirection } = {
      currentPage: currentPageParam,
      perPage: perPageParam,
      orderBy: orderByParam,
      orderDirection: orderDirectionParam,
      ...requestParams,
    };

    const total = payload.data?.item_count || 0;

    const paginationProps: PaginationProps = {
      itemCount: total,
      pageCount: payload.data?.page_count || 0,
      perPage: perPage,
      currentPage: currentPage,
      setPerPage: setPaginationLimit,
      setCurrentPage: (page: number) => {
        if (page > 0 && page <= total) {
          setPaginationPage(page);
        }
      },
      /* eslint-disable-next-line */
    };

    const sortingProps: SortingProps = {
      orderBy,
      orderDirection,
      setOrder,
    };

    useEffect(() => {
      if (requestParams == null) {
        setPayload({
          data: null,
          loading: false,
          error: null,
        });
        return;
      }

      const subscription = stream({
        page: currentPageParam,
        page_size: perPageParam,
        order_by: orderByParam,
        order_direction: orderDirectionParam,
        ...requestParams,
      }).subscribe({
        next: (res) => {
          setPayload({
            data: res,
            loading: false,
            error: null,
          });
        },
        error: (err) => {
          setPayload({
            data: null,
            loading: false,
            error: err,
          });
          console.error(err);
        },
      });

      return () => {
        subscription.unsubscribe();
      };
      /* eslint-disable-next-line */
    }, [
      currentPageParam,
      perPageParam,
      orderByParam,
      orderDirectionParam,
      JSON.stringify(requestParams),
      requestCounter,
    ]);

    return {
      loading: payload.loading,
      error: payload.error,
      data: payload.data?.data ?? null,
      paginationProps: {
        ...paginationProps,
        perPage: requestParams?.page_size ?? paginationProps.perPage,
      },
      sortingProps,
      reloadData,
    };
  };

export type ApiGetResult<Entity extends any> = {
  loading: boolean;
  error: string | null;
  data: Entity | null;
  reloadData: () => void;
};

const createGetHook =
  <Entity extends any, Params extends RequestParams & BatchGetRequestParams>(
    stream: (params: Params) => Observable<Entity>
  ) =>
  (requestParams: Params | null): ApiGetResult<Entity> => {
    const [requestCounter, setRequestCounter] = useState(0);
    const [payload, setPayload] = useState<{
      data: Entity | null;
      loading: boolean;
      error: string | null;
    }>({
      data: null,
      loading: true,
      error: null,
    });

    const reloadData = useCallback(() => {
      setRequestCounter((state) => state + 1);
    }, []);

    useEffect(() => {
      if (requestParams == null) {
        setPayload({
          data: null,
          loading: false,
          error: null,
        });
        return;
      }

      const subscription = stream(requestParams).subscribe({
        next: (res) => {
          if (res && res['error']) {
            setPayload({
              data: null,
              loading: false,
              error: res['error'].message,
            });
          } else {
            setPayload({
              data: res,
              loading: false,
              error: null,
            });
          }
        },
        error: (err) => {
          setPayload({
            data: null,
            loading: false,
            error: ErrorMessages.DefaulRpcErrorMessage,
          });
          console.error(err);
        },
      });

      return () => {
        subscription.unsubscribe();
      };
      /* eslint-disable-next-line */
    }, [requestCounter, JSON.stringify(requestParams)]);

    return {
      ...payload,
      reloadData,
    };
  };

export type ApiPostResult<
  Entity extends any,
  Params extends PostRequestParams,
  PostQueryRequestParams = undefined,
> = {
  loading: boolean;
  error: string | null;
  data: Entity | null;
  postData: (
    params: Params,
    queryRequestParams: PostQueryRequestParams
  ) => Promise<Entity | null>;
};

export const createPostHook =
  <
    Entity extends any,
    Params extends PostRequestParams,
    PostQueryRequestParams = undefined,
  >(
    stream: (
      params: Params,
      queryRequestParams?: PostQueryRequestParams
    ) => Observable<Entity>
  ) =>
  (): ApiPostResult<Entity, Params, PostQueryRequestParams> => {
    const [payload, setPayload] = useState<{
      data: Entity | null;
      loading: boolean;
      error: string | null;
    }>({
      data: null,
      loading: true,
      error: null,
    });

    const promise = new Promise<Entity | null>((resolve, reject) => {
      if (payload.error) {
        reject(payload.error);
      } else {
        resolve(payload.data);
      }
    });

    const postData = useCallback(
      (
        requestParams: Params,
        queryRequestParams?: PostQueryRequestParams
      ): Promise<any> => {
        const subscription = stream(
          requestParams,
          queryRequestParams
        ).subscribe({
          next: (res) => {
            setPayload({
              data: res,
              loading: false,
              error: null,
            });
          },
          error: (err) => {
            setPayload({
              data: null,
              loading: false,
              error: err,
            });
            console.error(err);
          },
        });

        return promise;
      },
      []
    );

    return {
      ...payload,
      postData,
    };
  };

/**
 * BLOCK */
export const usePaginatedGetBlocks = createPaginatedGetReducerHook(
  'getPaginatedBlocksSubscription',
  'paginatedBlocks',
  getPaginatedBlocksAsync
);

export const useGetBlock = createGetByHashReducerHook(
  'paginatedBlocks',
  getBlockByHashAsync
);

export const useGetPaginatedBlockDeploys = createPaginatedGetHook(
  'getPaginatedBlockDeploysSubscription',
  getPaginatedBlockDeploysSubscription
);

/**
 * @todo: looks like it's not used anymore
 */
export const useGetPaginatedBlockTransfers = createPaginatedGetHook(
  'getPaginatedBlockTransfersSubscription',
  getPaginatedBlockTransfersSubscription
);

export const useCurrencyRateAmount = createGetHook(getCurrencyRateSubscription);

/**
 * DEPLOY */
export const useGetPaginatedDeploys = createPaginatedGetReducerHook(
  'useGetPaginatedDeploys',
  'paginatedDeploys',
  getPaginatedDeploysAsync
);

/**
 * @todo: looks like it's not used anymore
 */
export const useGetPaginatedDeployTransfers = createPaginatedGetHook(
  'getPaginatedDeployTransfersSubscription',
  getPaginatedDeployTransfersSubscription
);

export const useGetDeploy = createGetByHashReducerHook(
  'paginatedDeploys',
  getDeployByHashAsync
);

export const useGetPaginatedDailyDeployNumbers = createGetHook(
  getPaginatedDailyDeployNumbersSubscription
);

export const useGetDeployRawData = createGetHook(getDeployRawDataSubscription);

/**
 * CONTRACT */
export const useGetContractPackageInfo = createGetHook(
  getContractPackageInfoSubscription
);

export const useGetContractInfo = createGetHook(getContractInfoSubscription);

export const useGetContractPackages = createPaginatedGetHook(
  'getPaginatedContractPackages',
  getPaginatedContractPackagesSubscription
);

export const useGetPaginatedContractPackageTokens = createPaginatedGetHook(
  'getPaginatedContractPackageTokens',
  getPaginatedContractPackageTokensSubscription
);

export const useGetContractNftToken = createGetHook(
  useGetContractNftTokenSubscription
);

export const useGetPaginatedNftTokenActivity = createPaginatedGetHook(
  'getPaginatedNftTokenActivitySubscription',
  getPaginatedNftTokenActivitySubscription
);

export const useGetPaginatedContractPackageNftOwners = createPaginatedGetHook(
  'getPaginatedContractPackageNftOwners',
  getPaginatedContractPackageNftOwnersSubscriptions
);

/**
 * ACCOUNT */
export const useGetPaginatedAccountDeploys = createPaginatedGetHook(
  'getPaginatedAccountDeploysSubscription',
  getPaginatedAccountDeploysSubscription
);

export const useGetPaginatedAccountTransfers = createPaginatedGetHook(
  'getPaginatedAccountTransfersSubscription',
  getPaginatedAccountTransfersSubscription
);

export const useGetActiveAccountDelegations = createPaginatedGetReducerHook(
  'getActiveAccountDelegationsSubscription',
  'activeAccountDelegations',
  getActiveAccountDelegationsAsync
);

export const useGetPaginatedAccountDelegations = createPaginatedGetReducerHook(
  'getPaginatedAccountDelegationsSubscription',
  'accountDelegations',
  getAccountDelegationsAsync
);

export const useGetPaginatedAccountTokens = createPaginatedGetHook(
  'getPaginatedAccountTokensSubscription',
  getPaginatedAccountTokensSubscription
);

export const useGetPaginatedAccountDelegatorRewards = createPaginatedGetHook(
  'getPaginatedAccountDelegatorRewardsSubscription',
  getPaginatedAccountDelegatorRewardsSubscription
);

export const useGetAccountsPublicKey = createPaginatedGetHook(
  'getAccountsPublicKeySubscription',
  getAccountsPublicKeySubscription
);

export const useGetAccountInfo = createGetHook(getAccountInfoSubscription);

export const usePostAccountsWithInfo = createPaginatedGetHook(
  'postAccountSubscription',
  postAccountSubscription
);

export const usePostAccounts = createPostHook(postAccountSubscription);

export const useGetAccountBalanceByPublicKey = createGetHook(
  getAccountBalanceByPublicKeySubscription
);

/**
 * CEP-18 **/
export const useGetAccountCep18TokenList = createPaginatedGetHook(
  'getAccountCep18TokenListSubscription',
  getAccountCep18TokenListSubscription
);

export const useGetContractPackageCep18TokenList = createPaginatedGetHook(
  'getContractPackageCep18TokenList',
  getContractPackageCep18TokenListSubscription
);

export const useGetPaginatedAccountCep18TxsList = createPaginatedGetHook(
  'getPaginatedAccountTokensTxsListSubscription',
  getPaginatedAccountTokensTxsListSubscription
);

export const useGetPaginatedContractPackageTokensTxsList =
  createPaginatedGetHook(
    'getPaginatedContractPackageTokensTxsListSubscription',
    getPaginatedContractPackageTokensTxsListSubscription
  );

/**
 * VALIDATOR **/
export const useGetValidatorAuctionMetrics = createGetHook(
  getValidatorAuctionMetricsSubscription
);

export const useGetValidatorsEraData = createPaginatedGetHook(
  'getPaginatedValidatorEraDataSubscription',
  getPaginatedValidatorEraDataSubscription
);

export const useGetValidatorsAuctionData = createPaginatedGetHook(
  'getPaginatedValidatorAuctionDataSubscription',
  getPaginatedValidatorAuctionDataSubscription
);

export const useGetPaginatedValidatorBlocks = createPaginatedGetHook(
  'getPaginatedValidatorBlocksSubscription',
  getPaginatedValidatorBlocksSubscription
);

export const useGetPaginatedValidatorRewards = createPaginatedGetHook(
  'getPaginatedValidatorRewardsSubscription',
  getPaginatedValidatorRewardsSubscription
);

export const useGetValidatorTotalRewards = createGetHook(
  getValidatorsTotalRewardsSubscription
);

export const useGetValidatorDelegatorsTotalRewards = createGetHook(
  getValidatorsTotalDelegatorRewardsSubscription
);

export const useGetValidatorsDelegations = createPaginatedGetHook(
  'getValidatorsDelegationsSubscription',
  getValidatorsDelegationsSubscription
);

/**
 * BIDDER **/
export const useGetBidderDetailsData = createGetHook(
  getBidderDetailsDataSubscription
);

/**
 * DELEGATOR **/
export const useGetDelegatorTotalRewards = createGetHook(
  getDelegatorsTotalRewardsSubscription
);

/**
 * UNISWAP **/
export const useGetFriendlyMarketTokenPairs = createGetHook(
  getFriendlyMarketTokenPairBatchSubscription
);

/**
 * CURRENCY **/

/**
 * ACTIVITY EXPORT */
export const useGetPaginatedAccountTokensByContractPackageHash =
  createPaginatedGetHook(
    'getAccountTokensByContractPackageHashSubscription',
    getPaginatedAccountTokensByContractPackageHashSubscription
  );

export const useGetActivityExportRequest = createPaginatedGetHook(
  'getPaginatedPaginatedActivityExportSubscription',
  getPaginatedActivityExportRequestSubscription
);

export const usePostActivityExportRequest = createPostHook(
  postActivityExportRequestSubscription
);

export const useCreateActivityExportDeployRequest = createPostHook(
  createActivityExportDeployRequestSubscription
);

export const useSubmitActivityExportDeployRequest = createPostHook(
  submitActivityExportDeployRequestSubscription
);

export const useGetAccountActivityDeployStatuses = createGetHook(
  getAccountActivityDeployStatusesSubscription
);

/**
 * ANALYTICS */
export const useGetNetworkStats = createGetHook(getNetworkStatsSubscription);
export const useGetRatesHistory = createGetHook(getRatesHistorySubscription);

/**
 * MULTISIG */
export const useSubmitMultisigDeployRequest = ({ networkMajorVersion }) => {
  const subscription =
    networkMajorVersion === '2'
      ? submitMultisigTransactionRequestSubscription
      : submitMultisigDeployRequestSubscription;

  return createPostHook(subscription)();
};

export const useAddSignatureToMultisigDeployRequest = createPostHook(
  addSignatureToMultisigTransactionRequestSubscription
);

export const useGetMultisigDeployRequest = ({ ...params }) => {
  const requestParams = isObjEmpty(params) ? null : params;

  // TODO: normalized types.
  //  After fully migrated to Condor, only transactions will be used
  //@ts-ignore
  return createGetHook(getMultisigTransactionRequestSubscription)(
    requestParams
  );
};

export const useGetKeysToWhichAccountAssociated = createPaginatedGetHook(
  'getKeysToWhichAccountAssociatedSubscription',
  getKeysToWhichAccountAssociatedSubscription
);

/**
 * ADS */
export const useAdsContactRequest = createPostHook(
  adsContactRequestSubscription
);

/**
 * UREF */
export const useGetPaginatedUrefTransfers = createPaginatedGetHook(
  'getPaginatedUrefTransfersSubscription',
  getUrefTransfersRequestSubscription
);

export const useGetPaginatedUrefDelegations = createPaginatedGetHook(
  'getPaginatedUrefDelegationsRequestSubscription',
  getPaginatedUrefDelegationsRequestSubscription
);

export const useGetPaginatedUrefRewards = createPaginatedGetHook(
  'getPaginatedUrefRewardsRequestSubscription',
  getPaginatedUrefRewardsRequestSubscription
);

export const useGetUrefDelegatorsTotalRewards = createGetHook(
  getUrefDelegatorsTotalRewardsSubscription
);

export const useGetUrefUndelegationBalance = createGetHook(
  getUrefUndelegationBalanceSubscription
);

/**
 * CSPR NAME */
export const useGetCsprNameInfo = createGetHook(
  getCsprNameInfoRequestSubscription
);
